/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the workspace.

   FormaDock hosts the canvas plus every tool panel, exactly the
   pattern the Designer proved (docs/DESIGNER_UNIFIED_PLAN.md §2.8):
   each panel is portalled into whichever container holds it — a dock
   slot, a floating box, or an anchored dropdown — never rewritten per
   host. Token chip selection + the copy/cut/paste triad (DESIGN.md §4
   ABSOLUTE) are wired here exactly as the Designer's workspace wires
   them, because the palette bodies are the SAME components
   (DropZone → ZoneChip / TokenFormatMenu) reading the SAME contexts.

   The SHEET is the primary surface for header/footer/pre/post work (Josh,
   2026-08-13, Order 1) and carries the FULL token model the classic canvas
   had: click a band, a column, or a token right on the paper; drag or
   click-insert from the Tokens/Project Tokens palettes; drop onto a token's
   edge to place it exactly (before / after / new line / replace); right-click
   a token for the format menu, a column for the clipboard triad; line breaks
   render as selectable ↵ markers. `onRequestTextEdit`, `onZoneQuickAdd`,
   `onZoneInsertAt` and `onZoneReplaceAt` below are what wire the paper up.

   There is NO "Header & Footer" panel. Every function the retired panel
   carried — precise placement, reordering, line breaks, per-column
   clipboard, per-token format/size/spacing — now lives ON THE PAPER in this
   editor's own idiom (dsg-* surfaces, semantic tokens, the shared
   `.dsg-menu`). Restoring the old panel would have meant restoring the old
   UI to get the function back; the function came back without it.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { getPanelData } from 'dockview';
import {
  Braces,
  Droplets,
  Flag,
  Hash,
  LayoutTemplate,
  ListOrdered,
  Ruler,
  Tags,
  Type,
} from 'lucide-react';
import type { EmbeddedImage, StandardConfig } from '../../shared/types';
import { FormaDock, type FormaDockHandle, type PaletteDef } from '../components/FormaDock';
import {
  CanvasSelectionContext,
  TokenClipboardContext,
  type CanvasSelectValue,
  type TokenClipboardOps,
} from '../components/TokenComponents';
import { DocSettings } from '../components/EditorComponents';
import {
  setPlaceholderMemories,
  setPlaceholderProjectMode,
  setPlaceholderValues,
  setProjectTokenContents,
  setProjectTokenLabels,
} from '../utils';
import { FormaService } from '../services/formaService';
import { reportError } from '../services/errorLog';
import { ToolPanel } from '../designer/ToolPanel';
import { PanelDropdown } from '../designer/PanelDropdown';
import {
  type BandKind,
  type PageVariant,
  type StyleSelection,
  type ZoneColumn,
  zoneAppend,
  zoneApplyToKeys,
  zoneDeleteKeys,
  zoneInsertAt,
  zoneKeyFor,
  zoneMoveTokens,
  zoneRemoveAt,
  zoneReplaceAt,
  readZone,
  parseTokenKey,
} from '../designer/formaZonesModel';
import {
  LayoutPalette,
  LevelPalette,
  ProjectTokensPalette,
  TextTokenDialog,
  TokensPalette,
  type TextDialogRequest,
} from '../designer/DesignPalettes';
import type { DesignWiring, ZoneDropData } from '../designer/Workspace';
import { isOverDockZone, type FloatingTool } from '../designer/Workspace';
import { WatermarkPanel } from './panels/WatermarkPanel';
import { EosPanel } from './panels/EosPanel';

export const STUDIO_PALETTES: PaletteDef[] = [
  { id: 'canvas', title: 'Paper', closable: false, noTab: true },
  {
    id: 'typography',
    title: 'Typography',
    width: 320,
    defaultPosition: { direction: 'right', reference: 'canvas' },
  },
  {
    id: 'margins',
    title: 'Page Margins',
    width: 320,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'formats',
    title: 'Formats',
    width: 320,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'layout',
    title: 'Layout Options',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'watermark',
    title: 'Watermark',
    width: 320,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'eos',
    title: 'Section End',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'tokens',
    title: 'Tokens',
    width: 340,
    defaultPosition: { direction: 'left', reference: 'canvas' },
  },
  // No "Header & Footer" panel: the SHEET is the surface for header/footer/
  // pre/post work (Josh, 2026-08-13, Order 1) and now carries the complete
  // token model, so a second list-shaped copy of the same three columns
  // would be old UI competing with the paper. See the header comment.
  {
    id: 'project-tokens',
    title: 'Project Tokens',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'tokens' },
  },
  {
    id: 'levels',
    title: 'Levels',
    width: 340,
    defaultClosed: true,
    defaultPosition: { direction: 'within', reference: 'tokens' },
  },
];

export const STUDIO_PANEL_ICONS: Record<string, React.ReactNode> = {
  typography: <Type size={15} aria-hidden="true" />,
  margins: <Ruler size={15} aria-hidden="true" />,
  formats: <Hash size={15} aria-hidden="true" />,
  layout: <LayoutTemplate size={15} aria-hidden="true" />,
  watermark: <Droplets size={15} aria-hidden="true" />,
  eos: <Flag size={15} aria-hidden="true" />,
  tokens: <Braces size={15} aria-hidden="true" />,
  'project-tokens': <Tags size={15} aria-hidden="true" />,
  levels: <ListOrdered size={15} aria-hidden="true" />,
};

/** One clipboard for token chips — module scope, same rule as the Designer's. */
let studioTokenClipboard: string[] = [];

export interface StudioWorkspaceProps {
  isDark: boolean;
  userId: string;
  locked: boolean;
  forma: StandardConfig | null;
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  canEdit: boolean;
  toast: (msg: string, isError?: boolean) => void;

  styleSel: StyleSelection;
  onStyleSelect: (sel: StyleSelection) => void;
  levelFocus: number | null;
  onLevelFocus: (level: number) => void;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
  onDragState: (on: boolean) => void;
  dragActive: boolean;

  floatingTools: FloatingTool[];
  onCloseFloating: (id: string) => void;
  onDockFloating: (id: string) => void;
  dropdown: { id: string; anchor: { left: number; bottom: number } } | null;
  onCloseDropdown: () => void;
  onFloatDropdown: (id: string, at: { left: number; top: number }) => void;
  onUndock: (id: string, at: { left: number; top: number }) => void;
  onDockPanelsChange: (ids: string[]) => void;
  /** Open a tool panel by id — the paper's end-of-section line uses it. */
  onOpenPanel: (id: string) => void;
  onDockReady: (handle: FormaDockHandle) => void;
  panelDragId: string | null;
  firmLayoutKey?: string;

  canvas: (wiring: DesignWiring) => React.ReactNode;
}

export function StudioWorkspace({
  isDark,
  userId,
  locked,
  forma,
  edit,
  canEdit,
  toast,
  styleSel,
  onStyleSelect,
  levelFocus,
  onLevelFocus,
  pageVariant,
  onPageVariant,
  onDragState,
  dragActive,
  floatingTools,
  onCloseFloating,
  onDockFloating,
  dropdown,
  onCloseDropdown,
  onFloatDropdown,
  onUndock,
  onDockPanelsChange,
  onOpenPanel,
  onDockReady,
  panelDragId,
  firmLayoutKey,
  canvas,
}: StudioWorkspaceProps) {
  const [dockEls, setDockEls] = React.useState<Record<string, HTMLElement>>({});
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);

  React.useEffect(() => {
    onDockPanelsChange(Object.keys(dockEls));
  }, [dockEls, onDockPanelsChange]);

  // ─── Chip selection + the copy/cut/paste triad (DESIGN.md §4 ABSOLUTE) ───
  const [selKey, setSelKey] = React.useState<string | null>(null);
  const [menuKey, setMenuKey] = React.useState<string | null>(null);
  const [selectedTokens, setSelectedTokens] = React.useState<string[]>([]);

  React.useEffect(() => {
    setSelKey(null);
    setMenuKey(null);
    setSelectedTokens([]);
  }, [pageVariant]);

  const select = React.useCallback<CanvasSelectValue['select']>((key, opts) => {
    if (!key) {
      setSelKey(null);
      setMenuKey(null);
      setSelectedTokens([]);
      return;
    }
    setSelKey(key);
    if (key.startsWith('token:')) {
      setSelectedTokens((prev) =>
        opts?.keepSelection
          ? prev.includes(key)
            ? prev
            : [...prev, key]
          : opts?.additive
            ? prev.includes(key)
              ? prev.filter((k) => k !== key)
              : [...prev, key]
            : [key]
      );
    } else {
      setSelectedTokens([]);
    }
    setMenuKey(opts?.menu ? key : null);
  }, []);

  const applyToSelection = React.useCallback(
    (fn: (value: string) => string) => {
      if (!selectedTokens.length) return;
      edit((f) => zoneApplyToKeys(f, selectedTokens, fn));
    },
    [edit, selectedTokens]
  );

  const moveTokens = React.useCallback(
    (targetZoneKey: string, keys: string[], copy: boolean) => {
      edit((f) => zoneMoveTokens(f, targetZoneKey, keys, copy));
      setSelKey(null);
      setMenuKey(null);
      setSelectedTokens([]);
    },
    [edit]
  );

  // Marquee-drag on the canvas hands over a whole SET at once (Ctrl/Shift
  // unions with what's already selected) — the classic canvas's
  // beginMarquee. `select(k, {additive})` can't express this: it toggles.
  const selectMany = React.useCallback<CanvasSelectValue['selectMany']>((keys, additive) => {
    const tokens = keys.filter((k) => k.startsWith('token:'));
    setMenuKey(null);
    setSelectedTokens((prev) => (additive ? [...new Set([...prev, ...tokens])] : tokens));
    setSelKey(tokens.length ? tokens[tokens.length - 1] : null);
  }, []);

  const canvasSelValue = React.useMemo<CanvasSelectValue>(
    () => ({ selKey, menuKey, selectedTokens, select, applyToSelection, moveTokens, selectMany }),
    [selKey, menuKey, selectedTokens, select, applyToSelection, moveTokens, selectMany]
  );

  const clipOps = React.useMemo<TokenClipboardOps>(() => {
    const valueAt = (k: string) => {
      if (!forma) return undefined;
      const { zoneKey, idx } = parseTokenKey(k);
      return readZone(forma, zoneKey)[idx];
    };
    return {
      copyKeys: (keys) => {
        const values = keys
          .filter((k) => k.startsWith('token:'))
          .map(valueAt)
          .filter((v): v is string => v != null);
        if (values.length) studioTokenClipboard = values;
      },
      cutKeys: (keys) => {
        const tokenKeys = keys.filter((k) => k.startsWith('token:'));
        const values = tokenKeys.map(valueAt).filter((v): v is string => v != null);
        if (values.length) studioTokenClipboard = values;
        edit((f) => zoneDeleteKeys(f, tokenKeys));
        select(null);
      },
      copySlot: (zoneKey) => {
        if (forma) studioTokenClipboard = [...readZone(forma, zoneKey)];
      },
      cutSlot: (zoneKey) => {
        if (!forma) return;
        const arr = readZone(forma, zoneKey);
        studioTokenClipboard = [...arr];
        edit((f) =>
          zoneDeleteKeys(
            f,
            arr.map((_v, i) => `token:${zoneKey}:${i}`)
          )
        );
      },
      pasteAt: (targetKey) => {
        if (!studioTokenClipboard.length) return;
        const values = [...studioTokenClipboard];
        if (targetKey.startsWith('token:')) {
          const { zoneKey, idx } = parseTokenKey(targetKey);
          edit((f) => {
            let next = f;
            values.forEach((v, i) => {
              next = zoneInsertAt(next, zoneKey, idx + 1 + i, v);
            });
            return next;
          });
        } else if (targetKey.startsWith('slot:')) {
          const zoneKey = targetKey.slice('slot:'.length);
          edit((f) => {
            let next = f;
            for (const v of values) next = zoneAppend(next, zoneKey, v);
            return next;
          });
        }
      },
      canPaste: () => studioTokenClipboard.length > 0,
    };
  }, [edit, forma, select]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt && (/^(input|textarea|select)$/i.test(tgt.tagName) || tgt.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;
      const tokenSel = selectedTokens.filter((k) => k.startsWith('token:'));
      const slotZone = selKey?.startsWith('slot:') ? selKey.slice('slot:'.length) : null;
      if (mod && (e.key === 'c' || e.key === 'C') && (tokenSel.length || slotZone)) {
        e.preventDefault();
        if (tokenSel.length) clipOps.copyKeys(tokenSel);
        else if (slotZone) clipOps.copySlot(slotZone);
      } else if (mod && (e.key === 'x' || e.key === 'X') && (tokenSel.length || slotZone)) {
        e.preventDefault();
        if (tokenSel.length) clipOps.cutKeys(tokenSel);
        else if (slotZone) clipOps.cutSlot(slotZone);
      } else if (mod && (e.key === 'v' || e.key === 'V') && (selKey || slotZone)) {
        if (selKey) {
          e.preventDefault();
          clipOps.pasteAt(selKey);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && tokenSel.length) {
        e.preventDefault();
        edit((f) => zoneDeleteKeys(f, tokenSel));
        select(null);
      } else if (e.key === 'Escape' && (selKey || menuKey || selectedTokens.length)) {
        select(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clipOps, edit, menuKey, select, selKey, selectedTokens]);

  // ─── Custom tokens (firm-level, same store as the classic view) ───
  const [savedCustomTokens, setSavedCustomTokens] = React.useState<string[]>([]);
  const [recentCustomTokens, setRecentCustomTokens] = React.useState<string[]>([]);
  React.useEffect(() => {
    let live = true;
    FormaService.loadTokens()
      .then((t) => {
        if (live) setSavedCustomTokens(t);
      })
      .catch((err) => reportError('Studio:loadTokens', err));
    return () => {
      live = false;
    };
  }, []);
  const persistSaved = (next: string[]) => {
    setSavedCustomTokens(next);
    FormaService.saveTokens(next).catch((err) => reportError('Studio:saveTokens', err));
  };
  const noteCustomToken = React.useCallback((t: string) => {
    setRecentCustomTokens((p) => [t, ...p.filter((x) => x !== t)].slice(0, 10));
  }, []);

  const [textDialog, setTextDialog] = React.useState<TextDialogRequest | null>(null);
  const requestImage = React.useCallback(async (): Promise<string | null> => {
    if (!window.api?.openImageDialog) {
      toast('Adding images needs the desktop app', true);
      return null;
    }
    const imgData = (await window.api.openImageDialog()) as EmbeddedImage | null;
    if (!imgData) return null;
    const imgId = 'img_' + Date.now().toString(36);
    edit((f) => ({ ...f, images: { ...(f.images || {}), [imgId]: imgData } }));
    return `{Image:${imgId}}`;
  }, [edit, toast]);

  // Master formas define {P#} placeholders but resolve nothing yet — a
  // project's copy is what binds them. Published during RENDER (not an
  // effect): the zone chips read these registries while they render.
  setPlaceholderProjectMode(false);
  setPlaceholderMemories(
    Object.fromEntries(Object.entries(forma?.placeholders || {}).map(([id, p]) => [id, p.was]))
  );
  setPlaceholderValues({});
  setProjectTokenLabels({});
  setProjectTokenContents({});

  const onZoneDrop = React.useCallback(
    (band: BandKind, col: ZoneColumn, d: ZoneDropData) => {
      const zoneKey = zoneKeyFor(band, pageVariant, col);
      onStyleSelect({ kind: 'zone', band });
      if (d.type === 'tokenMulti' && d.keys?.length) {
        moveTokens(zoneKey, d.keys, !!d.copy);
        return;
      }
      const value = d.value;
      if (value == null) return;
      if (d.type === 'token' && d.copy && d.sourceZone != null && d.sourceIndex != null) {
        edit((f) => zoneMoveTokens(f, zoneKey, [`token:${d.sourceZone}:${d.sourceIndex}`], true));
        return;
      }
      if (value === '__custom__') {
        const srcZ = d.sourceZone ?? null;
        const srcI = d.sourceIndex ?? null;
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            edit((f) => {
              let next = f;
              if (srcZ != null && srcI != null) next = zoneRemoveAt(next, srcZ, srcI);
              return zoneAppend(next, zoneKey, text);
            });
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && d.sourceZone == null) {
        void requestImage().then((tok) => {
          if (tok) edit((f) => zoneAppend(f, zoneKey, tok));
        });
        return;
      }
      edit((f) => {
        let next = f;
        if (d.sourceZone != null && d.sourceIndex != null) {
          next = zoneRemoveAt(next, d.sourceZone, d.sourceIndex);
        }
        return zoneAppend(next, zoneKey, value);
      });
    },
    [edit, moveTokens, noteCustomToken, onStyleSelect, pageVariant, requestImage]
  );

  // Click-to-add lands where the architect POINTED. A column selected on the
  // sheet wins; then a selected token (insert right after it); only then the
  // old band-level guess. Before the sheet had column regions, nothing could
  // ever select a column, so Left and Right were unreachable by click.
  const insertFromPalette = React.useCallback(
    (value: string) => {
      let zoneKey: string | null = null;
      let at: number | null = null;
      if (selKey?.startsWith('slot:')) {
        zoneKey = selKey.slice('slot:'.length);
      } else if (selKey?.startsWith('token:')) {
        const parsed = parseTokenKey(selKey);
        zoneKey = parsed.zoneKey;
        at = parsed.idx + 1;
      } else if (styleSel?.kind === 'zone' && forma) {
        const band = styleSel.band;
        const withContent = (['C', 'L', 'R'] as ZoneColumn[]).find(
          (c) => readZone(forma, zoneKeyFor(band, pageVariant, c)).length > 0
        );
        zoneKey = zoneKeyFor(band, pageVariant, withContent ?? 'C');
      }
      if (!zoneKey) {
        toast('Click a band or a column on the paper first, then click a chip to add it');
        return;
      }
      const zk = zoneKey;
      const idx = at;
      const place = (f: StandardConfig, v: string) =>
        idx == null ? zoneAppend(f, zk, v) : zoneInsertAt(f, zk, idx, v);
      if (value === '{Image}') {
        void requestImage().then((tok) => {
          if (tok) edit((f) => place(f, tok));
        });
        return;
      }
      edit((f) => place(f, value));
    },
    [edit, forma, pageVariant, requestImage, selKey, styleSel, toast]
  );

  // ─── Placing a dropped token EXACTLY (restored 2026-08-13) ───
  // A drop onto a token on the sheet inserts before/after it, starts a new
  // line above/below, or replaces it — the classic canvas's insertAtInZone /
  // replaceAtInZone. Without these every drop appended to the end of the
  // column, so reordering inside a column was impossible.
  const onZoneInsertAt = React.useCallback(
    (
      zoneKey: string,
      idx: number,
      value: string,
      srcZone: string | null,
      srcIdx: number | null,
      newLine: boolean
    ) => {
      if (value === '__custom__') {
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            edit((f) => zoneInsertAt(f, zoneKey, idx, text, srcZone, srcIdx, newLine));
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && srcZone == null) {
        void requestImage().then((tok) => {
          if (tok) edit((f) => zoneInsertAt(f, zoneKey, idx, tok, null, null, newLine));
        });
        return;
      }
      edit((f) => zoneInsertAt(f, zoneKey, idx, value, srcZone, srcIdx, newLine));
    },
    [edit, noteCustomToken, requestImage]
  );

  const onZoneReplaceAt = React.useCallback(
    (
      zoneKey: string,
      idx: number,
      value: string,
      srcZone: string | null,
      srcIdx: number | null
    ) => {
      if (value === '__custom__') {
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            edit((f) => zoneReplaceAt(f, zoneKey, idx, text, srcZone, srcIdx));
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && srcZone == null) {
        void requestImage().then((tok) => {
          if (tok) edit((f) => zoneReplaceAt(f, zoneKey, idx, tok, null, null));
        });
        return;
      }
      edit((f) => zoneReplaceAt(f, zoneKey, idx, value, srcZone, srcIdx));
    },
    [edit, noteCustomToken, requestImage]
  );

  // Order 1 (Josh, 2026-08-13): the empty-band hint on the paper is the fast
  // path to a zone's first token — the SAME literal-text dialog the panel
  // uses, into the COLUMN that was clicked. All three columns are offered, so
  // a Left-only header no longer needs a drag to get started.
  const onZoneQuickAdd = React.useCallback(
    (band: BandKind, col: ZoneColumn) => {
      const zoneKey = zoneKeyFor(band, pageVariant, col);
      onStyleSelect({ kind: 'zone', band });
      select(`slot:${zoneKey}`);
      setTextDialog({
        title: 'Add text',
        initial: '',
        onCommit: (text) => {
          if (!text) return;
          edit((f) => zoneAppend(f, zoneKey, text));
          noteCustomToken(text);
        },
      });
    },
    [edit, onStyleSelect, noteCustomToken, pageVariant, select]
  );

  const wiring: DesignWiring = {
    pageVariant,
    styleSel,
    onStyleSelect,
    dragActive,
    onZoneDrop,
    levelFocus,
    onRequestTextEdit: setTextDialog,
    onZoneQuickAdd,
    onZoneInsertAt,
    onZoneReplaceAt,
    onOpenPanel,
  };

  const renderPalette = React.useCallback(
    (id: string): React.ReactNode => {
      if (!forma) return null;
      switch (id) {
        case 'typography':
          return (
            <div className="sfs-docsettings-host">
              <DocSettings
                doc={forma.docSettings}
                onUpdate={(d) => edit((f) => ({ ...f, docSettings: d }))}
                sections={['Typography']}
              />
            </div>
          );
        case 'margins':
          return (
            <div className="sfs-docsettings-host">
              <DocSettings
                doc={forma.docSettings}
                onUpdate={(d) => edit((f) => ({ ...f, docSettings: d }))}
                sections={['Page Margins (inches)']}
              />
            </div>
          );
        case 'formats':
          return (
            <div className="sfs-docsettings-host">
              <DocSettings
                doc={forma.docSettings}
                onUpdate={(d) => edit((f) => ({ ...f, docSettings: d }))}
                sections={['Formats']}
              />
            </div>
          );
        case 'layout':
          return (
            <LayoutPalette
              forma={forma}
              onEdit={edit}
              pageVariant={pageVariant}
              onPageVariant={onPageVariant}
            />
          );
        case 'watermark':
          return <WatermarkPanel forma={forma} onEdit={edit} />;
        case 'eos':
          return <EosPanel forma={forma} onEdit={edit} />;
        case 'levels':
          return (
            <LevelPalette
              forma={forma}
              editor={null}
              levelFocus={levelFocus}
              onLevel={onLevelFocus}
              onEdit={edit}
              disabled={!canEdit}
            />
          );
        case 'tokens':
          return (
            <TokensPalette
              savedTokens={savedCustomTokens}
              recentTokens={recentCustomTokens}
              onSaveToken={(t) => persistSaved([...savedCustomTokens.filter((x) => x !== t), t])}
              onRemoveSavedToken={(t) => persistSaved(savedCustomTokens.filter((x) => x !== t))}
              onDragState={onDragState}
              onInsert={insertFromPalette}
            />
          );
        case 'project-tokens':
          return (
            <ProjectTokensPalette
              forma={forma}
              ctx={{}}
              projectTokens={[]}
              issuanceNames={[]}
              onEdit={edit}
              onDragState={onDragState}
              onInsert={insertFromPalette}
              masterAuthoring
            />
          );
        default:
          return null;
      }
    },
    [
      forma,
      edit,
      pageVariant,
      onPageVariant,
      levelFocus,
      onLevelFocus,
      canEdit,
      styleSel,
      requestImage,
      noteCustomToken,
      savedCustomTokens,
      recentCustomTokens,
      onDragState,
      insertFromPalette,
    ]
  );

  const titleOf = (id: string) => STUDIO_PALETTES.find((p) => p.id === id)?.title || id;

  const [floatPreview, setFloatPreview] = React.useState<{
    x: number;
    y: number;
    title: string;
  } | null>(null);
  const pendingFloatRef = React.useRef<{ id: string; at: { left: number; top: number } } | null>(
    null
  );
  const onUndockRef = React.useRef(onUndock);
  onUndockRef.current = onUndock;
  const panelDragIdRef = React.useRef(panelDragId);
  panelDragIdRef.current = panelDragId;
  const titleOfRef = React.useRef(titleOf);
  titleOfRef.current = titleOf;

  React.useEffect(() => {
    /** The id being dragged, and whether the dock owns it. */
    const dragging = (): { id: string; docked: boolean } | null => {
      const panelId = getPanelData()?.panelId;
      // The Paper is the anchored core — it never floats (FormaDock already
      // refuses to close it).
      if (panelId && panelId !== 'canvas') return { id: panelId, docked: true };
      const btn = panelDragIdRef.current;
      return btn ? { id: btn, docked: false } : null;
    };
    const onDragOver = (e: DragEvent) => {
      const drag = dragging();
      if (!drag) return;
      if (isOverDockZone(e.clientX, e.clientY)) {
        pendingFloatRef.current = null;
        setFloatPreview(null);
        return;
      }
      // Out here, letting go floats it — say so, and accept the drop so the
      // cursor stops reading "not allowed".
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      // Only a DOCKED panel is completed here; a button drag is finished by
      // the button's own dragend (it knows whether the dock took it).
      pendingFloatRef.current = drag.docked
        ? { id: drag.id, at: { left: e.clientX, top: e.clientY } }
        : null;
      setFloatPreview({ x: e.clientX, y: e.clientY, title: titleOfRef.current(drag.id) });
    };
    const settle = () => {
      const pending = pendingFloatRef.current;
      pendingFloatRef.current = null;
      setFloatPreview(null);
      if (pending) onUndockRef.current(pending.id, pending.at);
    };
    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('drop', settle, true);
    document.addEventListener('dragend', settle, true);
    return () => {
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('drop', settle, true);
      document.removeEventListener('dragend', settle, true);
    };
  }, []);

  return (
    <CanvasSelectionContext.Provider value={canvasSelValue}>
      <TokenClipboardContext.Provider value={clipOps}>
        <div className="dsg-workspace sfs-workspace">
          <FormaDock
            isDark={isDark}
            locked={locked}
            palettes={STUDIO_PALETTES}
            layoutKey={`sf.formaStudioDock.v1.${userId || 'anon'}`}
            firmLayoutKey={firmLayoutKey}
            onContainersChange={setDockEls}
            onReady={(h) => {
              dockHandleRef.current = h;
              onDockReady(h);
            }}
          />

          {dockEls.canvas && createPortal(canvas(wiring), dockEls.canvas)}

          {STUDIO_PALETTES.filter((p) => p.id !== 'canvas').map((p) =>
            dockEls[p.id] ? (
              <React.Fragment key={p.id}>
                {createPortal(renderPalette(p.id), dockEls[p.id])}
              </React.Fragment>
            ) : null
          )}

          {floatingTools
            .filter((f) => !dockEls[f.id])
            .map((f, i) => (
              <ToolPanel
                key={f.id}
                title={titleOf(f.id)}
                index={i}
                at={f.at}
                onDock={() => onDockFloating(f.id)}
                onClose={() => onCloseFloating(f.id)}
              >
                {renderPalette(f.id)}
              </ToolPanel>
            ))}

          {dropdown && !dockEls[dropdown.id] && (
            <PanelDropdown
              title={titleOf(dropdown.id)}
              anchor={dropdown.anchor}
              onFloat={() =>
                onFloatDropdown(dropdown.id, {
                  left: dropdown.anchor.left + 180,
                  top: dropdown.anchor.bottom + 14,
                })
              }
              onClose={onCloseDropdown}
            >
              {renderPalette(dropdown.id)}
            </PanelDropdown>
          )}

          {floatPreview &&
            createPortal(
              <div
                className="dsg-float-preview"
                aria-hidden="true"
                style={{ left: floatPreview.x, top: floatPreview.y }}
              >
                <span className="dsg-float-preview-bar">{floatPreview.title}</span>
                <span className="dsg-float-preview-hint">Release to float</span>
              </div>,
              document.body
            )}

          {textDialog && (
            <TextTokenDialog request={textDialog} onClose={() => setTextDialog(null)} />
          )}
        </div>
      </TokenClipboardContext.Provider>
    </CanvasSelectionContext.Provider>
  );
}
